<script setup>
import { computed, ref, watch } from 'vue';
import DeskToolbar from './components/DeskToolbar.vue';
import InternalNotesTimeline from './components/InternalNotesTimeline.vue';
import OperationalNotesKb from './components/OperationalNotesKb.vue';
import PluginSocket from './components/PluginSocket.vue';
import TicketActivityTimeline from './components/TicketActivityTimeline.vue';
import TicketComposer from './components/TicketComposer.vue';
import WritingAssistant from './components/WritingAssistant.vue';
import TicketList from './components/TicketList.vue';
import TicketThread from './components/TicketThread.vue';
import './desk-styles.css';

const props = defineProps({
    initialPayload: { type: Object, required: true },
    labels: { type: Object, default: () => ({}) },
    ticketLabels: { type: Object, default: () => ({}) },
    permissions: {
        type: Object,
        default: () => ({ operate: false, assign: false, merge: false, add_message: false }),
    },
    currentUserId: { type: [String, Number], default: '' },
    routes: { type: Object, default: () => ({}) },
    assistBusy: { type: Boolean, default: false },
    assistAction: { type: String, default: '' },
    assistEnabled: { type: Boolean, default: true },
    agentChat: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
    'save-operations', 'save-assignee', 'close-ticket', 'merge-ticket', 'send-message',
    'discard-draft', 'clear-draft-ui', 'ricrea', 'crea-bozza', 'stop-assist', 'select-ticket',
    'take-ownership', 'toast-error', 'change-status',
]);

const toolbarSource = () => props.initialPayload.desk_toolbar || {};
const initialAssignee = () => {
    const tb = toolbarSource();
    if (tb.assignee !== undefined && tb.assignee !== null) {
        return String(tb.assignee);
    }
    if (tb.assigned_user_id !== undefined && tb.assigned_user_id !== null) {
        return String(tb.assigned_user_id);
    }
    return '';
};
const initialPriority = () => String(toolbarSource().priority ?? '');
const initialText = () => props.initialPayload.composer?.text ?? props.initialPayload.composer?.reply_body ?? props.initialPayload.composer?.draft ?? '';
const initialType = () => {
    if (props.initialPayload.ticket?.is_open === false) {
        return 'note';
    }
    const raw = props.initialPayload.composer?.message_type ?? 'reply';
    if (raw === 'internal_note') {
        return 'note';
    }
    if (raw === 'customer_response') {
        return 'reply';
    }
    return raw;
};
const initialInstructions = () => props.initialPayload.composer?.instructions ?? '';

const assignee = ref(initialAssignee());
const priority = ref(initialPriority());
const messageText = ref(initialText());
const messageType = ref(initialType());
const instructions = ref(initialInstructions());
const mergeOpen = ref(false);
const mergeTarget = ref('');
const mergeReason = ref('');
const railOpen = ref(true);
const listOpen = ref(true);

const listToggleAriaLabel = computed(() => (
    listOpen.value
        ? (props.labels.hideTicketList || props.labels.hide_ticket_list || 'Nascondi ticket')
        : (props.labels.showTicketList || props.labels.show_ticket_list || 'Mostra ticket')
));

const railToggleAriaLabel = computed(() => (
    railOpen.value
        ? (props.labels.hideContext || props.labels.hide_context || 'Nascondi contesto')
        : (props.labels.showContext || props.labels.show_context || 'Mostra contesto')
));

watch(() => props.initialPayload, (next, prev) => {
    const nextTb = next?.desk_toolbar || {};
    const prevTb = prev?.desk_toolbar || {};
    const ticketChanged = next?.ticket?.id !== prev?.ticket?.id;
    const assigneeChanged = String(nextTb.assigned_user_id ?? nextTb.assignee ?? '')
        !== String(prevTb.assigned_user_id ?? prevTb.assignee ?? '');
    const priorityChanged = String(nextTb.priority ?? '') !== String(prevTb.priority ?? '');

    if (ticketChanged || assigneeChanged) {
        assignee.value = initialAssignee();
    }
    if (ticketChanged || priorityChanged) {
        priority.value = initialPriority();
    }

    messageText.value = initialText();
    messageType.value = next?.ticket?.is_open === false ? 'note' : initialType();
    instructions.value = initialInstructions();

    if (ticketChanged) {
        mergeOpen.value = false;
        mergeTarget.value = '';
        mergeReason.value = '';
    }
}, { deep: true });

watch(
    () => props.initialPayload?.ticket?.is_open,
    (isOpen) => {
        if (isOpen === false) {
            messageType.value = 'note';
        }
    },
);

const payload = computed(() => props.initialPayload || {});
const ticket = computed(() => payload.value.ticket || {});
const toolbar = computed(() => payload.value.desk_toolbar || {});
const tickets = computed(() => {
    const list = payload.value.ticket_list;
    if (Array.isArray(list)) {
        return list;
    }
    return list?.tickets ?? [];
});
const messages = computed(() => {
    const thread = payload.value.thread;
    if (Array.isArray(thread)) {
        return thread;
    }
    return thread?.messages ?? [];
});
const composer = computed(() => payload.value.composer || {});
const theme = computed(() => payload.value.theme || null);
const context = computed(() => payload.value.context || {});

const notes = computed(() => {
    const timeline = context.value.internal_notes_timeline;
    return Array.isArray(timeline) ? timeline : [];
});

const kbArticles = computed(() => context.value.operational_notes || context.value.kb_articles || []);

const activities = computed(() => {
    const raw = payload.value.activity;
    if (Array.isArray(raw)) {
        return raw;
    }
    if (raw && typeof raw === 'object') {
        return raw.activities || raw.ticket_activity || [];
    }
    return context.value.activities || [];
});

const forwardMerge = async (eventPayload) => {
    const target = mergeTargetById(eventPayload?.targetTicketId);
    const confirmed = await confirmMergeIfNeeded(target);
    if (!confirmed) {
        return;
    }

    emit('merge-ticket', eventPayload);
    mergeOpen.value = false;
};

const mergeTargetById = (targetId) => {
    const targets = toolbar.value.merge_targets || [];

    return targets.find((option) => String(option.value) === String(targetId)) || null;
};

const mergeNeedsConfirmation = (target) => {
    if (!target) {
        return false;
    }
    if (typeof target.needs_confirmation === 'boolean') {
        return target.needs_confirmation;
    }

    const sourceAssignee = String(toolbar.value.assigned_user_id ?? toolbar.value.assignee ?? '');
    const targetAssignee = target.assigned_user_id == null ? '' : String(target.assigned_user_id);
    const sourceEmail = String(ticket.value.customer_email || '').trim().toLowerCase();
    const targetEmail = String(target.customer_email || '').trim().toLowerCase();

    return sourceAssignee !== targetAssignee || sourceEmail !== targetEmail;
};

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fillMergeWho = (template, value) => String(template || ':value').replace(':value', value);

const mergeDisplayValue = (value, emptyLabel) => {
    const trimmed = String(value || '').trim();

    return trimmed !== '' ? trimmed : emptyLabel;
};

const sourceAssigneeLabel = () => {
    if (ticket.value.assigned_user_label) {
        return mergeDisplayValue(ticket.value.assigned_user_label, props.labels.unassigned || 'Non assegnato');
    }

    const id = String(toolbar.value.assigned_user_id ?? toolbar.value.assignee ?? '');
    const found = (toolbar.value.assignees || []).find((option) => String(option.value) === id);

    return mergeDisplayValue(found?.label, props.labels.unassigned || 'Non assegnato');
};

const mergeConfirmHtml = (target) => {
    const reasons = Array.isArray(target?.mismatch_reasons) ? target.mismatch_reasons : [];
    const assigneeMismatch = reasons.includes('assignee');
    const emailMismatch = reasons.includes('customer_email');
    const lines = [];

    if (assigneeMismatch && emailMismatch) {
        lines.push(props.labels.mergeConfirmAssigneeAndCustomerEmail
            || 'Stai unendo ticket di assegnatari e email cliente differenti.');
    } else if (assigneeMismatch) {
        lines.push(props.labels.mergeConfirmAssignee || 'Stai unendo ticket di assegnatari differenti.');
    } else if (emailMismatch) {
        lines.push(props.labels.mergeConfirmCustomerEmail || 'Stai unendo ticket di email cliente differenti.');
    } else {
        lines.push(props.labels.mergeConfirmAssigneeAndCustomerEmail
            || 'Stai unendo ticket di assegnatari e email cliente differenti.');
    }

    if (assigneeMismatch) {
        lines.push(fillMergeWho(
            props.labels.mergeConfirmAssigneeFrom || 'Operatore assegnato (da): :value',
            sourceAssigneeLabel(),
        ));
        lines.push(fillMergeWho(
            props.labels.mergeConfirmAssigneeTo || 'Operatore assegnato (a): :value',
            mergeDisplayValue(target?.assigned_user_label, props.labels.unassigned || 'Non assegnato'),
        ));
    }

    if (emailMismatch) {
        lines.push(fillMergeWho(
            props.labels.mergeConfirmCustomerEmailFrom || 'Email cliente (da): :value',
            mergeDisplayValue(ticket.value.customer_email, '—'),
        ));
        lines.push(fillMergeWho(
            props.labels.mergeConfirmCustomerEmailTo || 'Email cliente (a): :value',
            mergeDisplayValue(target?.customer_email, '—'),
        ));
    }

    const sentence = escapeHtml(lines[0] || '');
    const details = lines.slice(1).map(escapeHtml);
    const body = details.length > 0
        ? `${sentence}<br><br>${details.join('<br>')}`
        : sentence;

    return `<div style="text-align:left;width:100%">${body}</div>`;
};

const alignMergeConfirmLeft = (popup) => {
    const htmlContainer = popup.querySelector('.swal2-html-container, .swal2-content');
    const titleEl = popup.querySelector('.swal2-title');

    if (htmlContainer) {
        htmlContainer.style.textAlign = 'left';
        htmlContainer.style.justifyContent = 'flex-start';
        htmlContainer.style.alignItems = 'stretch';
        htmlContainer.style.display = 'block';
    }

    if (titleEl) {
        titleEl.style.textAlign = 'left';
    }
};

const confirmMergeIfNeeded = async (target) => {
    if (!mergeNeedsConfirmation(target)) {
        return true;
    }
    if (!window.Swal) {
        return true;
    }

    const result = await window.Swal.fire({
        title: props.labels.mergeConfirmTitle || 'Conferma unione',
        html: mergeConfirmHtml(target),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: props.labels.mergeAnyway || 'Unisci comunque',
        cancelButtonText: props.labels.cancel || 'Annulla',
        confirmButtonColor: '#d33',
        didOpen: alignMergeConfirmLeft,
    });

    return Boolean(result.isConfirmed);
};
</script>

<template>
    <div
        class="ticket-desk"
        :class="{
            'ticket-desk--rail-hidden': !railOpen,
            'ticket-desk--list-hidden': !listOpen,
        }"
        data-testid="ticket-show-desk"
        :data-desk-theme="theme?.key"
    >
        <TicketList
            :tickets="tickets"
            :active-ticket-id="ticket.id"
            :labels="labels"
            :channel-name="ticket.channel_name || ''"
            @select-ticket="emit('select-ticket', $event)"
        />
        <button
            type="button"
            class="ticket-desk-button ticket-desk-button--outline ticket-desk-toolbar__toggle ticket-desk-toolbar__toggle--list"
            :class="{ 'is-active': listOpen }"
            :aria-expanded="listOpen"
            :aria-label="listToggleAriaLabel"
            aria-controls="ticket-desk-ticket-list"
            data-testid="ticket-desk-list-toggle"
            @click="listOpen = !listOpen"
        >
            <span aria-hidden="true">☰</span>
        </button>

        <main class="ticket-desk__center">
            <DeskToolbar
                v-model:assignee="assignee"
                v-model:priority="priority"
                v-model:merge-open="mergeOpen"
                v-model:merge-target="mergeTarget"
                v-model:merge-reason="mergeReason"
                :ticket="ticket"
                :toolbar="toolbar"
                :labels="labels"
                :ticket-labels="ticketLabels"
                :permissions="permissions"
                :current-user-id="currentUserId"
                @save-operations="emit('save-operations', $event)"
                @save-assignee="emit('save-assignee', $event)"
                @close-ticket="emit('close-ticket', $event)"
                @change-status="emit('change-status', $event)"
                @merge-ticket="forwardMerge"
                @take-ownership="emit('take-ownership', $event)"
            />
            <div
                v-if="ticket.is_open === false"
                class="ticket-desk-closed-banner"
                data-testid="ticket-desk-closed-banner"
                role="status"
            >
                {{ labels.ticketClosed || labels.ticket_closed || 'Ticket chiuso.' }}
            </div>
            <TicketThread :messages="messages" :labels="labels" />
            <TicketComposer
                v-model:text="messageText"
                v-model:message-type="messageType"
                :mentionable-users="composer.mentionable_users || composer.mentionableUsers || []"
                :composer="composer"
                :labels="labels"
                :ticket-labels="ticketLabels"
                :can-add-message="Boolean(permissions.add_message)"
                :ticket-open="ticket.is_open !== false"
                :assist-busy="assistBusy"
                @send-message="emit('send-message', { ticketId: ticket.id, ...$event })"
                @discard-draft="emit('discard-draft', { ticketId: ticket.id })"
                @clear-draft-ui="emit('clear-draft-ui', { ticketId: ticket.id })"
                @toast-error="emit('toast-error', $event)"
            />
            <WritingAssistant
                v-model:instructions="instructions"
                :text="messageText"
                :message-type="messageType"
                :composer="composer"
                :labels="labels"
                :ticket-labels="ticketLabels"
                :assist-busy="assistBusy"
                :assist-action="assistAction"
                :assist-enabled="assistEnabled"
                :agent-chat="agentChat"
                @ricrea="emit('ricrea', { ticketId: ticket.id, ...$event })"
                @crea-bozza="emit('crea-bozza', { ticketId: ticket.id, ...$event })"
                @stop-assist="emit('stop-assist')"
            />
        </main>
        <button
            type="button"
            class="ticket-desk-button ticket-desk-button--outline ticket-desk-toolbar__toggle ticket-desk-toolbar__toggle--rail"
            :class="{ 'is-active': railOpen }"
            :aria-expanded="railOpen"
            :aria-label="railToggleAriaLabel"
            aria-controls="ticket-desk-rail"
            data-testid="ticket-desk-rail-toggle"
            @click="railOpen = !railOpen"
        >
            <span aria-hidden="true">☰</span>
        </button>

        <aside id="ticket-desk-rail" class="ticket-desk__rail">
            <InternalNotesTimeline :notes="notes" :labels="labels" />
            <PluginSocket
                slot-name="rail"
                :ticket-id="ticket.id"
                :can-operate="Boolean(permissions.operate)"
                :customer-email="ticket.customer_email || ticket.customer?.email || ''"
            />
            <OperationalNotesKb :articles="kbArticles" :labels="labels" />
            <TicketActivityTimeline :activities="activities" :labels="labels" />
        </aside>
    </div>
</template>
